import { createSlice } from '@reduxjs/toolkit';
import { parsePanelCard } from '../panelFocus';

// scheduled-output-screen slug: the scheduled-output board (/s/<id>/scheduled)
// accumulates cards across many task fires with NO per-turn reset, so the panel
// would grow unbounded over a long-lived kiosk session. Cap it to the most
// recent N entries. A normal chat turn resets via eventsStale and never
// approaches this, so the cap only ever truncates the long-lived board.
const MAX_PANEL_EVENTS = 200;

// Live events (ADR 2026-11-03-live-events-and-links): running cards kept apart from the panel's
// output, by id. The agent caps running ones; ended ones are kept a while so their links can say so.
const MAX_LIVE = 10;
const MAX_LIVE_KEPT = 50;
const noLive = () => ({ items: [], active: null });

// The card an output value carries for a live event, or null.
// A name for an event whose card has no title; the agent names it the same way.
export function liveLabel(kind) {
  const name = String(kind || '').split('.')[0].replace(/[_-]/g, ' ').trim();
  return name ? name[0].toUpperCase() + name.slice(1) : 'Live card';
}

export function liveCardOf(evt) {
  let v = evt;
  if (typeof v === 'string') {
    if (v[0] !== '{') return null;
    try { v = JSON.parse(v); } catch (e) { return null; }
  }
  return v && typeof v === 'object' && typeof v.live_event === 'string' ? v : null;
}

// Move a batch's live cards into `state.live`; returns the rest. Output from a turn brings its
// event to the front; a replay only restores it. Ordinary output alone shows the panel's output.
function takeLive(state, evts, replay) {
  const rest = [];
  let anyLive = false;
  evts.forEach((e) => {
    const card = liveCardOf(e);
    if (!card) { rest.push(e); return; }
    anyLive = true;
    const id = card.live_event;
    const item = state.live.items.find((i) => i.id === id);
    if (item) {
      item.card = card;
      item.ended = false;
    } else {
      // The viewer it came with sizes it; later output switches the panel's viewer, not its.
      state.live.items.push({ id, kind: card.kind, title: card.title || card.name || liveLabel(card.kind),
                              card, ended: false, viewer: state.viewer,
                              ...(typeof card.live_of === 'string' && { of: card.live_of }) });
    }
    if (!replay) state.live.active = id;
  });
  const running = state.live.items.filter((i) => !i.ended);
  running.slice(0, Math.max(0, running.length - MAX_LIVE)).forEach((i) => { i.ended = true; });
  if (state.live.items.length > MAX_LIVE_KEPT) {
    const drop = state.live.items.length - MAX_LIVE_KEPT;
    let dropped = 0;
    state.live.items = state.live.items.filter((i) => !(i.ended && dropped < drop && ++dropped));
  }
  if (!anyLive && !replay && rest.length) state.live.active = null;
  return rest;
}

const chatSlice = createSlice({
  name: 'chat',
  initialState: {
    messages: [],
    user: {},
    // WS lifecycle: 'connecting' (initial open + awaiting SUBSCRIBER_METRICS),
    // 'live' (handshake complete, frames flowing), 'stale' (WS closed; user
    // must tap to reconnect). Per the ws-reconnect slug, we deliberately
    // don't auto-reconnect — the user re-engages explicitly so message-loss
    // is visible rather than hidden by retry loops.
    connectionStatus: 'connecting',
    // background-run (!666 review): bumped on every SCHEDULED_NOTIFICATION,
    // BEFORE the station gate decides whether the result belongs in this
    // conversation. The Scheduled Tasks page watches it and re-reads; whether
    // the run is "for" this session is a separate question from whether the
    // page it is showing has gone stale.
    scheduledRunSeq: 0,
    station: {},
    threadId: null,
    // Stashed when the WS closes — sent back as params.thread_id on the
    // next assist:resume_thread frame so the agent rehydrates the prior
    // ConversationLog instead of starting a fresh thread.
    pendingResumeThreadId: null,
    events: [],
    eventsStale: false,
    live: noLive(),
    // card-sandbox-messaging-push: the latest kind-targeted card push
    // ({ kind, message, seq }); a sandboxed card watches this and relays a
    // matching-kind push to its iframe's onPush. Null until the first push.
    cardPush: null,
    viewer: 'text',
    waiting: false,
    prompting: false,
    widget: null,  // {type, ...settings} for active input widget
    // chat-helper-completion slug (#3): names of other visitors currently
    // typing. Ephemeral — added on a TYPING frame, removed on a timeout (the
    // WS middleware owns the timers) or when that person's message lands. Never
    // persisted, never part of the message list.
    typingPeers: [],
    // The viewer's aliases in this room ({byAccount, byName}, roomAliases.js).
    roomAliases: { byAccount: {}, byName: {} },
    // chat-helper-completion slug (#5): a transient system notice (e.g.
    // "Reconnected") rendered as a non-persisted line that auto-clears. Shares
    // the ephemeral-notice surface with the typing indicator; never enters
    // `messages`, so it can't replay on reconnect or load_older.
    transientNotice: null,
    // A notice floated over the chat for this viewer (meta.notice); never in `messages`.
    floatingNotice: null,
    // ephemeral-status-beats: a transient assistant "working…" status (e.g.
    // "checking…"). Fed by an `ephemeral` message frame OR a viewer's action
    // confirmation; shown in a status line and NEVER added to `messages`, so it
    // never persists or replays. Cleared when the real reply lands. Mirrors the
    // "X is typing" indicator.
    assistantStatus: null,
    // output-second-screen slug: the owner's `?split` share token (set on a
    // SPLIT_TOKEN frame); the SPA renders it as the /s/<id>/<token> link/QR.
    splitToken: null,
    // output-second-screen slug: observer-side join status — null (n/a),
    // 'observing' (joined), or {denied: true, reason} (rejected).
    observeStatus: null,
  },
  reducers: {
    addEvent: (state, action) => {
      // Push a single event string into the viewer-panel queue. Used by
      // the WS middleware when a MAIL arrives with meta.viewer != 'text'
      // and a kind-routed structured payload — those cards belong in
      // the right-side viewer (GameViewer / SimulatorViewer), not the
      // chat scroll.
      const evt = action.payload;
      if (evt == null) return;
      if (!takeLive(state, [evt], false).length) return;
      if (state.eventsStale) {
        state.events = [evt];
        state.eventsStale = false;
      } else {
        state.events = [...state.events, evt];
      }
    },
    // Cards the server couldn't link (no account, a failed save): the chat links to the page's own
    // copy of what the panel holds, gone on reload like the rest of an unsaved chat.
    addLocalLink: (state, action) => {
      const cards = state.events.filter((e) => parsePanelCard(e));
      if (!cards.length) return;
      const { displayName } = action.payload || {};
      state.messages.push({ userId: 0, displayName, timestamp: Date.now(), message: '',
                            isLocal: false, isSystem: false, room: false, localCards: cards });
    },
    addMessage: (state, action) => {
      let payload = action.payload;
      let options = null;
      let events = null;

      // chat-ux-polish /clear: wipe the scrollback + output panel before this
      // (confirmation) message lands, so the conversation visibly resets.
      if (payload.clear) {
        state.messages = [];
        state.events = [];
        state.eventsStale = false;
        state.live = noLive();
      }

      if (payload.actions && payload.actions.length > 0) {
        const action = payload.actions[0]
        let params = action.params || {}
        options = params["message"] && params["message"].options
        events = params["history"] && params["history"].options
      }
      if (Array.isArray(events) && events.length > 0) {
        events = takeLive(state, events, !!payload.replay);
      }

      // Right-panel events accumulate across the conversation turn — the
      // orchestrator streams structured cards / progress as each fan-out
      // result lands. Submit-time markEventsStale() defers the actual
      // clear until the FIRST new event lands so the prior turn's cards
      // stay visible while the user is waiting for the new turn to start
      // producing output (instead of going blank between submit and the
      // first event_callback).
      if (Array.isArray(events) && events.length > 0) {
        if (state.eventsStale) {
          state.events = [...events];
          state.eventsStale = false;
        } else {
          state.events = [...state.events, ...events];
        }
        // Bound the panel (see MAX_PANEL_EVENTS): keep only the most recent
        // entries so the never-resetting scheduled board can't grow unbounded.
        if (state.events.length > MAX_PANEL_EVENTS) {
          state.events = state.events.slice(-MAX_PANEL_EVENTS);
        }
      }

      // Don't push an empty row to the chat panel when the message body is
      // missing (events-only update). Earlier behavior treated any payload
      // with events as a message, producing blank lines on every progress
      // update.
      if (!payload.message) return;

      // station-chat-subscriptions slug: dedup by stable msgId so a reconnect's
      // join-replay doesn't duplicate messages already on screen (from a prior
      // replay or their live broadcast). meta.msgId = SQLite row id.
      if (payload.msgId) {
        if (state.messages.some((m) => m.msgId === payload.msgId)) {
          return;
        }
        // Reconcile the joiner's OWN messages: ones typed live this session
        // render as a local echo with no server id, so the replayed copy
        // (mine, with a msgId) wouldn't dedup and would double. Match the
        // first un-id'd local echo by text and upgrade its msgId in place
        // rather than appending. Replay is chronological, so repeated text
        // reconciles in order.
        if (payload.isLocal) {
          const echo = state.messages.find(
            (m) => m.isLocal && !m.msgId && m.message === payload.message
          );
          if (echo) {
            echo.msgId = payload.msgId;
            return;
          }
        }
      }

      const entry = {
        msgId: payload.msgId,
        userId: payload.userId,
        displayName: payload.displayName,
        // chat-helper-completion slug (#2): prefer the server's wall-clock
        // (meta.ts, an ISO8601 string) so a message shows its ORIGINAL time —
        // critical for join/load_older replay, where Date.now() would mis-stamp
        // historical messages as "just now". Falls back to receive-time for
        // frames without a server ts (e.g. the local echo of the user's own
        // input, and AI replies).
        timestamp: payload.ts ? Date.parse(payload.ts) : Date.now(),
        message: payload.message,
        isLocal: payload.isLocal || false,
        isSystem: payload.isSystem || false,
        // station-broadcast slug: a message from another live visitor of
        // the station (or a StationChat announcement), rendered sender-
        // tagged and distinct from the AI's own replies.
        room: payload.room || false,
        // A room message's author account (meta.senderAccount), so the page can show
        // the viewer's alias for them (roomAliases.js).
        senderAccount: payload.senderAccount || null,
        // scheduled-result-view: a finished background run — `{runId, taskId,
        // href, status, cardCount}`. Its presence switches the renderer to the
        // "View result ↗" affordance instead of a prose bubble.
        //
        // THIS ENTRY IS AN ALLOWLIST, not a spread of `payload`. A field the
        // middleware dispatches but that is not named here is dropped silently
        // and the message renders as bare text — which is exactly what happened
        // on the first live run of this slug, while every unit test passed,
        // because they assert on the dispatched ACTION and never on what
        // survives the reducer.
        scheduledRun: payload.scheduledRun,
        options: options,
        events: events,
      };
      // chat-helper-completion slug (#3): a message from someone clears their
      // "is typing" state immediately — they just sent it. (No-op for AI
      // replies / own echoes whose name isn't in the typing set.)
      if (payload.displayName) {
        state.typingPeers = state.typingPeers.filter((n) => n !== payload.displayName);
      }
      // station-chat-load-older: a backfill page prepends to the TOP (older
      // history). The agent emits the page newest-first, so unshifting each
      // lands them chronologically above the existing scroll. Live/join
      // messages append as usual.
      if (payload.older) {
        state.messages.unshift(entry);
      } else {
        state.messages.push(entry);
      }
    },
    removeOptions: (state, action) => {
      const newArray = state.messages.map(({ ["options"]: _, ...rest }) => rest);
      state.messages = newArray
    },
    setUser: (state, action) => {
      state.user = action.payload;
    },
    notedScheduledRun: (state) => {
      state.scheduledRunSeq += 1;
    },
    setStation: (state, action) => {
      state.station = {
        station: action.payload.metrics.station.station,
        sessionId: action.payload.sessionId
      }
    },
    setThreadId: (state, action) => {
      state.threadId = action.payload;
    },
    clearEvents: (state) => {
      state.events = [];
      state.eventsStale = false;
      state.live = noLive();
    },
    liveEnded: (state, action) => {
      const item = state.live.items.find((i) => i.id === action.payload);
      if (item) item.ended = true;
      // A room's event this page never held: kept so its link can say it has ended.
      else state.live.items.push({ id: action.payload, ended: true });
      // One on screen stays there until the user moves to something else.
    },
    // Show a running live event in the panel; null shows the panel's ordinary output.
    setActiveLive: (state, action) => {
      state.live.active = action.payload || null;
    },
    markEventsStale: (state) => {
      // Soft-clear: existing events stay visible until the first event of
      // the next turn lands. addMessage's reducer consumes the flag.
      state.eventsStale = true;
    },
    setViewer: (state, action) => {
      state.viewer = action.payload;
    },
    setWaiting: (state, action) => {
      state.waiting = action.payload;
    },
    setPrompting: (state, action) => {
      state.prompting = action.payload;
    },
    setConnectionStatus: (state, action) => {
      state.connectionStatus = action.payload;
    },
    setPendingResumeThreadId: (state, action) => {
      state.pendingResumeThreadId = action.payload;
    },
    setSimFact: (state, action) => {
      state.simFact = action.payload;
    },
    setCardPush: (state, action) => {
      // card-sandbox-messaging-push: store the latest { kind, message, seq }. The
      // `seq` guarantees a fresh payload identity so an identical repeated push
      // still re-triggers the receiving card's onPush effect.
      state.cardPush = action.payload;
    },
    setWidget: (state, action) => {
      state.widget = action.payload;
    },
    // chat-helper-completion slug (#3): typing presence. The middleware adds a
    // peer on each TYPING frame (resetting that peer's clear-timer) and removes
    // them on timeout. De-duped so two quick pings don't list a name twice.
    addTypingPeer: (state, action) => {
      const name = action.payload;
      if (name && !state.typingPeers.includes(name)) {
        state.typingPeers = [...state.typingPeers, name];
      }
    },
    removeTypingPeer: (state, action) => {
      state.typingPeers = state.typingPeers.filter((n) => n !== action.payload);
    },
    // chat-helper-completion slug (#3): drop all typing presence at once — used
    // when the socket closes, so a peer who was mid-type when we disconnected
    // doesn't leave a stuck "… is typing…" line until its per-peer timer fires.
    clearTypingPeers: (state) => {
      state.typingPeers = [];
    },
    setRoomAliases: (state, action) => {
      state.roomAliases = action.payload || { byAccount: {}, byName: {} };
    },
    // chat-helper-completion slug (#5): transient system notice (auto-cleared
    // by the middleware on a timer). null hides it.
    setTransientNotice: (state, action) => {
      state.transientNotice = action.payload;
    },
    setFloatingNotice: (state, action) => {
      state.floatingNotice = action.payload;
    },
    // ephemeral-status-beats: set (string) / clear (null) the transient assistant
    // status line. Never touches `messages`, so it can't persist or replay.
    setAssistantStatus: (state, action) => {
      state.assistantStatus = action.payload;
    },
    // output-second-screen slug.
    setSplitToken: (state, action) => {
      state.splitToken = action.payload;
    },
    setObserveStatus: (state, action) => {
      state.observeStatus = action.payload;
    },
  },
});

export const {
  addEvent,
  addMessage,
  addLocalLink,
  removeOptions,
  clearEvents,
  liveEnded,
  setActiveLive,
  markEventsStale,
  setUser,
  notedScheduledRun,
  setStation,
  setThreadId,
  setViewer,
  setWaiting,
  setPrompting,
  setConnectionStatus,
  setPendingResumeThreadId,
  setSimFact,
  setCardPush,
  setWidget,
  addTypingPeer,
  removeTypingPeer,
  clearTypingPeers,
  setRoomAliases,
  setTransientNotice,
  setFloatingNotice,
  setAssistantStatus,
  setSplitToken,
  setObserveStatus
} = chatSlice.actions;

export default chatSlice.reducer;